import { useEffect, useState } from 'react';
import shippingService from '../../api/services/shipping.service';
import StepItem from '../StepItem/StepItem';
import './OrderTracking.css';

const TRACKING_STEPS = [
  { key: 'ORDER_PLACED', label: 'Order Placed' },
  { key: 'SHIPPED', label: 'Shipped' },
  { key: 'OUT_FOR_DELIVERY', label: 'Out for Delivery' },
  { key: 'DELIVERED', label: 'Delivered' },
];

const STATUS_TO_STEP = {
  ORDER_PLACED: 0,
  SHIPPED: 1,
  OUT_FOR_DELIVERY: 2,
  DELIVERED: 3,
};

const STATUS_ALIASES = {
  PRE_TRANSIT: 'ORDER_PLACED',
  LABEL_CREATED: 'ORDER_PLACED',
  LABEL_PURCHASED: 'ORDER_PLACED',
  CREATED: 'ORDER_PLACED',
  INFO_RECEIVED: 'ORDER_PLACED',
  UNKNOWN: 'ORDER_PLACED',
  TRANSIT: 'SHIPPED',
  SHIPPED: 'SHIPPED',
  IN_TRANSIT: 'SHIPPED',
  MANIFEST_PICKUP: 'SHIPPED',
  EXCEPTION: 'SHIPPED',
  AVAILABLE_FOR_PICKUP: 'OUT_FOR_DELIVERY',
  OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
  DELIVERY_ATTEMPTED: 'OUT_FOR_DELIVERY',
  DELIVERED: 'DELIVERED',
};

const ORDER_STATUS_ALIASES = {
  PENDING: 'ORDER_PLACED',
  PAID: 'ORDER_PLACED',
  PROCESSING: 'ORDER_PLACED',
  CONFIRMED: 'ORDER_PLACED',
  CREATED: 'ORDER_PLACED',
  SHIPPING_CREATED: 'ORDER_PLACED',
  PACKED: 'SHIPPED',
  SHIPPED: 'SHIPPED',
  IN_TRANSIT: 'SHIPPED',
  OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
  COMPLETED: 'DELIVERED',
  DELIVERED: 'DELIVERED',
};

const cleanText = (value) => {
  if (value === null || value === undefined) {
    return '';
  }

  return String(value)
    .trim()
    .replace(/^`+|`+$/g, '')
    .trim();
};

const normalizeStatusToken = (value) =>
  cleanText(value)
    .replace(/[\s-]+/g, '_')
    .toUpperCase();

const mapTrackingStatus = (value) => STATUS_ALIASES[normalizeStatusToken(value)] || null;

const mapOrderStatus = (value) => ORDER_STATUS_ALIASES[normalizeStatusToken(value)] || 'ORDER_PLACED';

const getShippingDetails = (order) => order?.shipping_details || order?.shipping || order?.shipment || {};

// The tracking endpoint is keyed by the ORD- uuid
const getOrderUuidFromOrder = (order) => {
  if (order?.order_uuid) return cleanText(order.order_uuid);
  if (typeof order?.uuid === 'string' && order.uuid.startsWith('ORD-')) return cleanText(order.uuid);
  if (typeof order?.id === 'string' && order.id.startsWith('ORD-')) return cleanText(order.id);
  return cleanText(order?.uuid || order?.id);
};

const getTrackingNumberFromOrder = (order) => {
  const shippingDetails = getShippingDetails(order);

  return cleanText(
    shippingDetails?.tracking_number ||
      order?.tracking_number ||
      order?.tracking?.tracking_number ||
      order?.shipping?.tracking_number
  );
};

const getCarrierFromOrder = (order) => {
  const shippingDetails = getShippingDetails(order);

  return cleanText(
    shippingDetails?.carrier ||
      order?.carrier ||
      order?.shipping_carrier ||
      order?.tracking?.carrier ||
      order?.shipping?.carrier
  );
};


const getTrackingNumber = (trackingData, order) =>
  cleanText(
    trackingData?.tracking_number ||
      trackingData?.tracking_code ||
      trackingData?.trackingNumber ||
      trackingData?.tracking?.tracking_number
  ) || getTrackingNumberFromOrder(order);

const getCarrier = (trackingData, order) =>
  cleanText(
    trackingData?.carrier ||
      trackingData?.carrier_name ||
      trackingData?.provider ||
      trackingData?.tracking?.carrier
  ) || getCarrierFromOrder(order);

const getEta = (trackingData, order) => {
  const shippingDetails = getShippingDetails(order);

  return (
    trackingData?.eta ||
    trackingData?.estimated_delivery ||
    trackingData?.expected_delivery ||
    trackingData?.estimated_delivery_date ||
    shippingDetails?.estimated_delivery_date ||
    order?.estimated_delivery_date ||
    ''
  );
};

const formatDateTime = (value) => {
  const cleanValue = cleanText(value);

  if (!cleanValue) {
    return '';
  }

  const date = new Date(cleanValue);

  if (Number.isNaN(date.getTime())) {
    return cleanValue;
  }

  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

// "Friday, 29 August" - the delivery-date headline
const formatDeliveryDate = (value) => {
  const cleanValue = cleanText(value);
  if (!cleanValue) return '';

  const date = new Date(cleanValue);
  if (Number.isNaN(date.getTime())) return cleanValue;

  return date.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });
};

const formatDayHeading = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  const today = new Date();
  const isSameDay = date.toDateString() === today.toDateString();
  if (isSameDay) return 'Today';

  return date.toLocaleDateString([], { weekday: 'long', day: 'numeric', month: 'long' });
};

const formatTime = (value) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';

  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

// Shippo sends location as an object; other carriers sometimes send a string
const getEventLocation = (event) => {
  const location = event?.location || event?.address || event?.tracking_location;
  if (!location) return '';
  if (typeof location === 'string') return cleanText(location);

  return [location.city, location.state, location.zip, location.country]
    .map((part) => cleanText(part))
    .filter(Boolean)
    .join(', ');
};

const normalizeEvent = (event) => ({
  date:
    event?.status_date ||
    event?.object_created ||
    event?.datetime ||
    event?.timestamp ||
    event?.date ||
    '',
  status: cleanText(event?.status || event?.tracking_status || event?.event || ''),
  details: cleanText(event?.status_details || event?.message || event?.description || ''),
  location: getEventLocation(event),
});

// Newest first, grouped under a day heading
const groupEventsByDay = (events) => {
  const normalized = events
    .map(normalizeEvent)
    .filter((event) => event.date || event.details || event.status)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const groups = [];
  normalized.forEach((event) => {
    const heading = formatDayHeading(event.date) || 'Earlier';
    const lastGroup = groups[groups.length - 1];

    if (lastGroup && lastGroup.heading === heading) {
      lastGroup.events.push(event);
    } else {
      groups.push({ heading, events: [event] });
    }
  });

  return groups;
};

const getTrackingUrl = (trackingData) =>
  cleanText(
    trackingData?.tracking_url_provider ||
      trackingData?.tracking_url ||
      trackingData?.public_url
  );

// Shippo TEST keys return a developer message that must never reach a customer
const isTestMode = (trackingData) => trackingData?.test_mode === true;

const EXCEPTION_STATUSES = ['EXCEPTION', 'FAILURE', 'RETURNED', 'CANCELLED', 'ERROR'];

const isExceptionStatus = (value) => EXCEPTION_STATUSES.includes(normalizeStatusToken(value));

const formatCarrierLabel = (value) => {
  const label = cleanText(value);

  if (!label) {
    return '';
  }

  return label.length <= 5 ? label.toUpperCase() : label;
};

const getHistoryEvents = (trackingData) => {
  if (Array.isArray(trackingData?.tracking_history)) {
    return trackingData.tracking_history;
  }

  if (Array.isArray(trackingData?.history)) {
    return trackingData.history;
  }

  if (Array.isArray(trackingData?.activities)) {
    return trackingData.activities;
  }

  if (Array.isArray(trackingData?.checkpoints)) {
    return trackingData.checkpoints;
  }

  if (Array.isArray(trackingData?.tracking?.history)) {
    return trackingData.tracking.history;
  }

  return [];
};

const getCurrentApiStatus = (trackingData) =>
  trackingData?.tracking_status?.status ||
  trackingData?.current_status ||
  trackingData?.shipment_status ||
  trackingData?.status ||
  trackingData?.tracking?.status ||
  '';

const getCurrentApiStatusDate = (trackingData) =>
  trackingData?.tracking_status?.status_date ||
  trackingData?.tracking_status?.object_created ||
  trackingData?.status_date ||
  trackingData?.updated_at ||
  trackingData?.last_tracking_update ||
  '';

const getCurrentStatusDetails = (trackingData) =>
  cleanText(
    trackingData?.tracking_status?.status_details ||
      trackingData?.status_details ||
      trackingData?.tracking?.status_details ||
      trackingData?.latest_update
  );

const buildStepMetaMap = (order, trackingData, resolvedStatus) => {
  const history = getHistoryEvents(trackingData);
  const stepMetaMap = {
    ORDER_PLACED: formatDateTime(order?.created_at || order?.createdAt),
    SHIPPED: '',
    OUT_FOR_DELIVERY: '',
    DELIVERED: '',
  };

  history.forEach((event) => {
    const mappedStatus = mapTrackingStatus(
      event?.status || event?.tracking_status || event?.event || event?.status_code
    );
    const eventDate =
      event?.status_date ||
      event?.object_created ||
      event?.datetime ||
      event?.created_at;

    if (mappedStatus && !stepMetaMap[mappedStatus]) {
      stepMetaMap[mappedStatus] = formatDateTime(eventDate);
    }
  });

  if (!stepMetaMap[resolvedStatus]) {
    stepMetaMap[resolvedStatus] = formatDateTime(getCurrentApiStatusDate(trackingData));
  }

  return stepMetaMap;
};

const buildTrackingState = (order, trackingData) => {
  const apiStatus = mapTrackingStatus(getCurrentApiStatus(trackingData));
  const orderStatus =
    order?.order_status || order?.status || order?.shipping_status || order?.shipment_status;
  const resolvedStatus = apiStatus || mapOrderStatus(orderStatus);
  const currentStepIndex = STATUS_TO_STEP[resolvedStatus] ?? 0;
  const stepMetaMap = buildStepMetaMap(order, trackingData, resolvedStatus);

  return TRACKING_STEPS.map((step, index) => ({
    ...step,
    meta: stepMetaMap[step.key],
    isCompleted: index <= currentStepIndex,
    isCurrent: index === currentStepIndex && resolvedStatus !== 'DELIVERED',
  }));
};

const getStatusLabel = (stepKey) =>
  TRACKING_STEPS.find((step) => step.key === stepKey)?.label || 'Order Placed';

// `trackingData` may be supplied by a parent that already fetched it (the
// tracking page), in which case this component skips its own request.
export default function OrderTracking({
  order,
  trackingData: providedTrackingData,
  loading: providedLoading = false,
  error: providedError = '',
}) {
  const isControlled = providedTrackingData !== undefined;
  const [fetchedTrackingData, setFetchedTrackingData] = useState(null);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [fetchError, setFetchError] = useState('');
  const [showActivity, setShowActivity] = useState(true);
  const [copied, setCopied] = useState(false);

  const trackingData = isControlled ? providedTrackingData : fetchedTrackingData;
  const loading = isControlled ? providedLoading : fetchLoading;
  const error = isControlled ? providedError : fetchError;

  const orderUuid = getOrderUuidFromOrder(order);

  useEffect(() => {
    if (isControlled) return undefined;

    let isMounted = true;

    const loadTracking = async () => {
      if (!orderUuid) {
        setFetchedTrackingData(null);
        setFetchError('');
        setFetchLoading(false);
        return;
      }

      setFetchLoading(true);
      setFetchError('');

      try {
        const data = await shippingService.getTrackingByOrder(orderUuid);

        if (isMounted) {
          setFetchedTrackingData(data);
        }
      } catch (apiError) {
        if (isMounted) {
          setFetchedTrackingData(null);
          setFetchError(apiError.message || 'Unable to load tracking details right now.');
        }
      } finally {
        if (isMounted) {
          setFetchLoading(false);
        }
      }
    };

    loadTracking();

    return () => {
      isMounted = false;
    };
  }, [orderUuid, isControlled]);

  const steps = buildTrackingState(order, trackingData);
  const currentStep =
    steps.find((step) => step.isCurrent) ||
    steps.filter((step) => step.isCompleted).slice(-1)[0] ||
    steps[0];
  const trackingNumber = getTrackingNumber(trackingData, order);
  const carrier = formatCarrierLabel(getCarrier(trackingData, order));
  const serviceName = cleanText(
    trackingData?.selected_rate?.servicelevel?.name || trackingData?.servicelevel?.name
  );
  const trackingUrl = getTrackingUrl(trackingData);
  const testMode = isTestMode(trackingData);
  const apiStatus = getCurrentApiStatus(trackingData);
  const hasException = isExceptionStatus(apiStatus);
  const isDelivered = currentStep?.key === 'DELIVERED';

  // The status_details field carries a Shippo dev message while on a test key
  const latestUpdate = testMode ? '' : getCurrentStatusDetails(trackingData);
  const eventGroups = groupEventsByDay(getHistoryEvents(trackingData));

  const deliveryDate = formatDeliveryDate(getEta(trackingData, order));
  const deliveredOn = isDelivered ? formatDateTime(getCurrentApiStatusDate(trackingData)) : '';

  const headline = isDelivered
    ? deliveredOn ? `Delivered ${deliveredOn}` : 'Delivered'
    : hasException
      ? 'Delivery needs attention'
      : deliveryDate
        ? `Arriving ${deliveryDate}`
        : trackingNumber
          ? 'On its way'
          : 'Preparing your order';

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(trackingNumber);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard blocked - the number is selectable on screen anyway
    }
  };

  if (loading) {
    return (
      <section className="order-tracking-card is-loading" aria-label="Loading tracking">
        <span className="tracking-skeleton tracking-skeleton-headline" />
        <span className="tracking-skeleton tracking-skeleton-bar" />
        <span className="tracking-skeleton tracking-skeleton-strip" />
      </section>
    );
  }

  return (
    <section className={`order-tracking-card${hasException ? ' has-exception' : ''}${isDelivered ? ' is-delivered' : ''}`}>
      <div className="tracking-headline-row">
        <div className="tracking-headline-copy">
          <h4 className="tracking-headline">{headline}</h4>
          {latestUpdate ? <p className="tracking-subline">{latestUpdate}</p> : null}
        </div>
        <span className="tracking-status-pill">{getStatusLabel(currentStep?.key)}</span>
      </div>

      {error ? <div className="order-tracking-feedback is-error">{error}</div> : null}

      {!error && !trackingNumber ? (
        <div className="order-tracking-feedback">
          We'll show live tracking here as soon as your parcel is handed to the carrier.
        </div>
      ) : null}

      <div className="order-tracking-steps" aria-label="Order tracking progress">
        {steps.map((step, index) => (
          <StepItem
            key={step.key}
            title={step.label}
            meta={step.meta}
            isCompleted={step.isCompleted}
            isCurrent={step.isCurrent}
            isLast={index === steps.length - 1}
          />
        ))}
      </div>

      {trackingNumber ? (
        <div className="tracking-carrier-strip">
          <div className="tracking-carrier-info">
            <span className="tracking-carrier-name">
              {carrier || 'Carrier'}
              {serviceName ? <span className="tracking-service">{serviceName}</span> : null}
            </span>
            <span className="tracking-number">{trackingNumber}</span>
          </div>

          <div className="tracking-carrier-actions">
            <button type="button" className="tracking-action" onClick={handleCopy}>
              {copied ? 'Copied' : 'Copy'}
            </button>
            {trackingUrl ? (
              <a
                className="tracking-action is-primary"
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Track with {carrier || 'carrier'}
              </a>
            ) : null}
          </div>
        </div>
      ) : null}

      {eventGroups.length > 0 ? (
        <div className="tracking-activity">
          <button
            type="button"
            className="tracking-activity-toggle"
            onClick={() => setShowActivity((prev) => !prev)}
            aria-expanded={showActivity}
          >
            <span>Shipment activity</span>
            <span className={`tracking-chevron${showActivity ? ' is-open' : ''}`} aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9" />
              </svg>
            </span>
          </button>

          {showActivity ? (
            <div className="tracking-activity-body">
              {eventGroups.map((group) => (
                <div key={group.heading} className="tracking-day-group">
                  <p className="tracking-day-heading">{group.heading}</p>
                  {group.events.map((event, index) => (
                    <div key={`${group.heading}-${index}`} className="tracking-event">
                      <span className="tracking-event-time">{formatTime(event.date)}</span>
                      <span className="tracking-event-dot" aria-hidden="true" />
                      <span className="tracking-event-copy">
                        <span className="tracking-event-title">
                          {event.details || getStatusLabel(mapTrackingStatus(event.status))}
                        </span>
                        {event.location ? (
                          <span className="tracking-event-location">{event.location}</span>
                        ) : null}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : trackingNumber ? (
        <p className="tracking-no-scans">
          No carrier scans yet. Updates appear here once the parcel is scanned.
        </p>
      ) : null}
    </section>
  );
}


